// CTSK-290 — per-tool Activity tab: the install/troubleshooting audit
// trail read from GET /tools/:id/activity.
//
// Why: operators re-installing tool manifests had no per-tool
// visibility into what happened when + why — re-installs silently
// no-op on existing rows (the project_bundle_importer_skip_if_exists
// pain), so "did my change land?" had no answer in the UI. This tab
// renders every audit row targeting the tool, newest first.
//
// v1 deliberately has NO filter/sort (brief): chronological DESC,
// 25/page, cursor-based Prev/Next. The Detail column expands to the
// raw {before, after, message} payload as pretty-printed JSON —
// troubleshooting means seeing exactly what the audit table holds.
//
// Rendered by the slug-routed native tool pages (Data Sources
// Registry + Timeline Lane) as a synthetic tab appended after their
// file-driven tab strips — captain-tasko has no generic /tools/:slug
// detail page; those two pages ARE the tool detail surface today.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiList } from '../lib/api.js';

export interface ToolActivityEntry {
  id: string;
  timestamp: string;
  actor: { id: string; name: string | null; email: string | null } | null;
  action: string;
  payload: {
    before: Record<string, unknown> | null;
    after: Record<string, unknown> | null;
    message: string | null;
  };
  sourceTag: string | null;
}

/** Same local relative-format helper shape the sibling components use
 *  (AttachmentsPanel, TagAuditSection, …) — a shared util does not
 *  exist yet and this matches their output wording. */
function formatRelative(iso: string, now = Date.now()): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

function formatAbsolute(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  return then.toLocaleString();
}

export function ToolActivityTab({ toolId }: { toolId: string }) {
  // Cursor stack: entry N is the cursor that produced page N+2 (page 1
  // has no cursor). Prev pops; Next pushes the current page's
  // nextCursor. Plain state — leaving the tab resets to page 1, which
  // is the right reading posture for an audit trail.
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack.length > 0 ? cursorStack[cursorStack.length - 1] : undefined;

  const [expandedId, setExpandedId] = useState<string | null>(null);

  const activityQ = useQuery({
    queryKey: ['tool-activity', toolId, cursor ?? null],
    queryFn: () =>
      apiList<ToolActivityEntry>(`/tools/${toolId}/activity`, {
        query: { cursor },
      }),
    enabled: !!toolId,
  });

  const entries = activityQ.data?.data ?? [];
  const nextCursor = activityQ.data?.page.nextCursor ?? null;

  if (activityQ.isLoading) {
    return <div style={{ padding: 20, color: 'var(--text-muted)' }}>Loading activity…</div>;
  }

  if (activityQ.isError) {
    return (
      <div style={{ padding: 16, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}>
        Failed to load activity for this tool.
      </div>
    );
  }

  if (entries.length === 0 && cursorStack.length === 0) {
    return (
      <div
        data-testid="tool-activity-empty"
        style={{ padding: 16, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}
      >
        No activity recorded for this tool.
      </div>
    );
  }

  return (
    <div data-testid="tool-activity-tab">
      <table className="grid" style={{ width: '100%', tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ width: 110 }}>When</th>
            <th style={{ width: 160 }}>Actor</th>
            <th style={{ width: 200 }}>Action</th>
            <th style={{ width: 160 }}>Source</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const expanded = expandedId === e.id;
            return (
              <tr key={e.id} style={{ verticalAlign: 'top' }}>
                <td title={formatAbsolute(e.timestamp)} style={{ whiteSpace: 'nowrap' }}>
                  {formatRelative(e.timestamp)}
                </td>
                <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.actor?.email ?? undefined}>
                  {e.actor ? e.actor.name ?? e.actor.id : <span style={{ color: 'var(--text-muted)' }}>system</span>}
                </td>
                <td>
                  <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{e.action}</code>
                </td>
                <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.sourceTag ?? undefined}>
                  {e.sourceTag ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
                </td>
                <td>
                  {e.payload.message && (
                    <div style={{ fontSize: 12, marginBottom: expanded ? 6 : 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {e.payload.message}
                    </div>
                  )}
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setExpandedId(expanded ? null : e.id)}
                    style={{ background: 'none', border: 'none', color: 'var(--accent-primary)', cursor: 'pointer', padding: 0, fontSize: 12, textDecoration: 'underline' }}
                  >
                    {expanded ? 'Hide payload' : 'Show payload'}
                  </button>
                  {expanded && (
                    <pre
                      data-testid={`activity-payload-${e.id}`}
                      style={{
                        margin: '6px 0 0 0',
                        padding: 8,
                        background: 'var(--surface-sunken)',
                        borderRadius: 4,
                        fontSize: 11,
                        fontFamily: 'var(--font-mono)',
                        overflowX: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 320,
                        overflowY: 'auto',
                      }}
                    >
                      {JSON.stringify(e.payload, null, 2)}
                    </pre>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Cursor pager — Prev walks back down the stack, Next follows
          the server's nextCursor. Page-number jumping needs totals the
          keyset cursor deliberately doesn't compute. */}
      {(cursorStack.length > 0 || nextCursor) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, fontSize: 13 }}>
          <button
            type="button"
            disabled={cursorStack.length === 0}
            onClick={() => { setCursorStack((s) => s.slice(0, -1)); setExpandedId(null); }}
            style={{ padding: '4px 12px', border: '1px solid var(--border-strong)', borderRadius: 4, background: 'var(--surface-raised)', cursor: cursorStack.length === 0 ? 'default' : 'pointer', opacity: cursorStack.length === 0 ? 0.5 : 1 }}
          >
            ‹ Prev
          </button>
          <span style={{ color: 'var(--text-muted)' }}>Page {cursorStack.length + 1}</span>
          <button
            type="button"
            disabled={!nextCursor}
            onClick={() => { if (nextCursor) { setCursorStack((s) => [...s, nextCursor]); setExpandedId(null); } }}
            style={{ padding: '4px 12px', border: '1px solid var(--border-strong)', borderRadius: 4, background: 'var(--surface-raised)', cursor: nextCursor ? 'pointer' : 'default', opacity: nextCursor ? 1 : 0.5 }}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
}
